import { Component, OnInit } from '@angular/core';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DrawerModule } from 'primeng/drawer';
import { AccordionModule } from 'primeng/accordion';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { PaginatorModule } from 'primeng/paginator';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { RoleService, Role, RoleWithPermissions, Subscriber, Permission, PagedSubscriberRequest, PagedSubscriberResponse } from '../../../core/services/role.service';
import { PermissionService } from '../../../core/services/permission.service';
import { UserService, User } from '../../../core/services/user.service';
import { AuthService } from '../../../core/services/auth';
import { SubscriptionContextService, SubscriptionContext } from '../../../core/services/subscription-context.service';
import { InputIcon } from 'primeng/inputicon';
import { IconFieldModule } from 'primeng/iconfield';

interface SelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-role-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    TagModule,
    DrawerModule,
    AccordionModule,
    SelectModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    HttpClientModule,
    ToastModule,
    ToggleSwitchModule,
    PaginatorModule,
    IconFieldModule,
    InputIcon,
    ConfirmDialogModule,
    TooltipModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './role-management.html',
  styleUrl: './role-management.scss',
})
export class RoleManagement implements OnInit {
  roles: RoleWithPermissions[] = [];
  globalRoles: RoleWithPermissions[] = [];
  subscriberRoles: { [subscriberId: string]: RoleWithPermissions[] } = {};
  permissions: Permission[] = [];
  permissionCategories: string[] = [];
  permissionsByCategory: { [category: string]: Permission[] } = {};
  selectedRole: RoleWithPermissions | null = null;
  drawerVisible: boolean = false;
  drawerMode: 'view' | 'add' = 'view';
  users: User[] = [];
  subscribers: Subscriber[] = [];
  subscriberOptions: SelectOption[] = [];
  currentUserRole: string = '';
  selectedSubscription: SubscriptionContext | null = null;
  
  // Pagination properties (1-based indexing for backend compatibility)
  currentPage = 1;
  pageSize = 10;
  pageSizes = [10, 20, 50, 100];
  totalSubscribers = 0;
  loading = false;
  globalRolesLoading = false;
  subscriberRolesLoading = false;
  
  // Search properties
  searchText = '';
  
  // Math property for template
  Math = Math;
  
  // Form properties
  roleForm: FormGroup;
  availableBaseRoles: SelectOption[] = [];
  selectedPermissions: { [key: string]: boolean } = {};
  baseRolePermissions: { [key: string]: Permission[] } = {}; // Store base role permissions
  selectedBaseRole: string = '';

  constructor(
    private roleService: RoleService,
    private permissionService: PermissionService,
    private userService: UserService,
    private authService: AuthService,
    private subscriptionContextService: SubscriptionContextService,
    private fb: FormBuilder,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {
    this.roleForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      baseRole: ['', Validators.required],
      permissions: this.fb.group({})
    });
    
    // Listen for base role changes
    this.roleForm.get('baseRole')?.valueChanges.subscribe(baseRole => {
      if (baseRole) {
        this.onBaseRoleChange(baseRole);
      }
    });
  }

  ngOnInit(): void {
    this.getCurrentUserRole();
    this.initializePermissions();
    this.loadSubscribers();
    this.loadGlobalRoles();
    this.subscribeToSubscriptionContext();
  }

  // Method to refresh current user role (useful if user data changes)
  refreshCurrentUserRole(): void {
    this.getCurrentUserRole();
  }

  private initializePermissions(): void {
    // Fetch permissions and categories
    this.permissionService.getAllPermissions().subscribe({
      next: (perms: Permission[]) => {
        if (!Array.isArray(perms)) {
          console.error('Permissions is not an array:', perms);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load permissions - invalid data format'
          });
          return;
        }
        
        this.permissions = perms;
        this.permissionCategories = [...new Set(perms.map((p: Permission) => p.category))];
        this.permissionsByCategory = {};
        for (const cat of this.permissionCategories) {
          this.permissionsByCategory[cat] = perms.filter((p: Permission) => p.category === cat);
        }
        
        // Initialize selected permissions
        perms.forEach(perm => {
          this.selectedPermissions[perm._id] = false;
        });
        
      },
      error: (error: any) => {
        console.error('Error loading permissions:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load permissions'
        });
      }
    });
  }

  private loadSubscribers(): void {
    this.loadPagedSubscribers();
  }

  private loadPagedSubscribers(): void {
    // Ensure valid pagination parameters
    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
    if (this.pageSize < 1) {
      this.pageSize = 10;
    }
    
    this.loading = true;
    const request: PagedSubscriberRequest = {
      pageIndex: this.currentPage, // Backend uses 1-based indexing
      pageSize: this.pageSize,
      filters: {
        searchText: this.searchText.trim() || undefined
      }
    };

    // Log the request for debugging
    console.log('Loading paged subscribers with request:', request);

    this.roleService.getPagedSubscribers(request).subscribe({
      next: (response: PagedSubscriberResponse) => {
        this.subscribers = response.data;
        this.totalSubscribers = response.metadata[0]?.total || 0;
        this.loading = false;
        console.log('Subscribers loaded successfully:', response);
        // Load subscriber roles after subscribers are loaded
        this.loadSubscriberRoles();
      },
      error: (error: any) => {
        console.error('Error loading subscribers:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.errorResponse?.errmsg || 'Failed to load subscribers'
        });
        this.loading = false;
        
        // Reset to safe values on error
        this.currentPage = 1;
        this.subscribers = [];
        this.totalSubscribers = 0;
      }
    });
  }

  private loadGlobalRoles(): void {
    // Fetch global roles
    this.globalRolesLoading = true;
    this.roleService.getGlobalRoles().subscribe({
      next: (roles: RoleWithPermissions[]) => {
        this.globalRoles = roles;
        this.updateAvailableBaseRoles();
        this.globalRolesLoading = false;
        // User counts are now included in the role data from the backend
      },
      error: (error: any) => {
        console.error('Error loading global roles:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load global roles'
        });
        this.globalRolesLoading = false;
      }
    });
  }

  private loadSubscriberRoles(): void {
    // Fetch subscriber roles
    this.subscriberRolesLoading = true;
    let completedRequests = 0;
    const totalRequests = this.subscribers.length;
    
    if (totalRequests === 0) {
      this.subscriberRolesLoading = false;
      return;
    }
    
    this.subscribers.forEach(sub => {
      this.roleService.getSubscriberRoles(sub._id).subscribe({
        next: (roles: RoleWithPermissions[]) => {
          this.subscriberRoles[sub._id] = roles;
          // User counts are now included in the role data from the backend
          completedRequests++;
          if (completedRequests === totalRequests) {
            this.subscriberRolesLoading = false;
          }
        },
        error: (error: any) => {
          console.error(`Error loading roles for subscriber ${sub._id}:`, error);
          completedRequests++;
          if (completedRequests === totalRequests) {
            this.subscriberRolesLoading = false;
          }
        }
      });
    });
  }

  private updateAvailableBaseRoles(): void {
    this.availableBaseRoles = [];
    
    if (this.currentUserRole === 'CSM' || this.currentUserRole === 'SM') {
      // CSM and SM can select any role as base
      const globalRoles = [
        { label: 'Chief System Manager', value: 'CSM' },
        { label: 'System Manager', value: 'SM' }
      ];
      const subscriberRoles = [
        { label: 'Super Administrator', value: 'SA' },
        { label: 'Administrator', value: 'A' },
        { label: 'Field Service Representative', value: 'FSR' }
      ];
      
      if (this.hasSelectedSubscription()) {
        // Show subscriber level roles when subscription is selected
        this.availableBaseRoles = subscriberRoles;
      } else {
        // Show only global roles when no subscription is selected
        this.availableBaseRoles = globalRoles;
      }
    } else if (this.currentUserRole === 'SA') {
      // SA can select A and FSR as base, but only when subscription is selected
      if (this.hasSelectedSubscription()) {
        this.availableBaseRoles = [
          { label: 'Administrator', value: 'A' },
          { label: 'Field Service Representative', value: 'FSR' }
        ];
      } else {
        // Don't show subscriber level roles when no subscription is selected
        this.availableBaseRoles = [];
      }
    } else if (this.currentUserRole === 'A') {
      // A can select FSR as base, but only when subscription is selected
      if (this.hasSelectedSubscription()) {
        this.availableBaseRoles = [
          { label: 'Field Service Representative', value: 'FSR' }
        ];
      } else {
        // Don't show subscriber level roles when no subscription is selected
        this.availableBaseRoles = [];
      }
    } else if (this.currentUserRole === 'SUPER_DEV_ADMIN') {
      // SUPER_DEV_ADMIN can select any role as base
      const globalRoles = [
        { label: 'Chief System Manager', value: 'CSM' },
        { label: 'System Manager', value: 'SM' }
      ];
      const subscriberRoles = [
        { label: 'Super Administrator', value: 'SA' },
        { label: 'Administrator', value: 'A' },
        { label: 'Field Service Representative', value: 'FSR' }
      ];
      
      if (this.hasSelectedSubscription()) {
        // Show subscriber level roles when subscription is selected
        this.availableBaseRoles = subscriberRoles;
      } else {
        // Show only global roles when no subscription is selected
        this.availableBaseRoles = globalRoles;
      }
    }
  }

  getCurrentUserRole(): void {
    try {
      const currentUser = this.authService.getLoggedInUser();
      if (currentUser && currentUser.role) {
        // The role object should have an identification field
        this.currentUserRole = currentUser.role.identification || '';
        console.log('Current user role:', this.currentUserRole);
      } else {
        console.warn('No user role found, defaulting to empty string');
        this.currentUserRole = '';
      }
    } catch (error) {
      console.error('Error getting current user role:', error);
      this.currentUserRole = '';
    }
  }

  canEditGlobalRoles(): boolean {
    return this.currentUserRole === 'CSM' || this.currentUserRole === 'SM' || this.currentUserRole === 'SUPER_DEV_ADMIN';
  }

  canAddCustomRole(): boolean {
    return this.currentUserRole === 'CSM' || this.currentUserRole === 'SM' || this.currentUserRole === 'SA' || this.currentUserRole === 'A' || this.currentUserRole === 'SUPER_DEV_ADMIN';
  }

  openDrawer(role: RoleWithPermissions | null, mode: 'view' | 'add' = 'view'): void {
    this.selectedRole = role;
    this.drawerMode = mode;
    this.drawerVisible = true;
    
    if (mode === 'add') {
      this.resetForm();
    }
  }

  closeDrawer(): void {
    this.drawerVisible = false;
    this.selectedRole = null;
    this.resetForm();
  }

  private resetForm(): void {
    this.roleForm.reset();
    const permissionsGroup = this.roleForm.get('permissions') as FormGroup;
    Object.keys(permissionsGroup.controls).forEach(key => {
      permissionsGroup.removeControl(key);
    });
  }



  getPermissionControl(permissionId: string): any {
    const permissionsGroup = this.roleForm.get('permissions') as FormGroup;
    if (!permissionsGroup.contains(permissionId)) {
      permissionsGroup.addControl(permissionId, this.fb.control(false));
    }
    return permissionsGroup.get(permissionId);
  }

  onSubmit(): void {
    if (this.roleForm.valid) {
      const formValue = this.roleForm.value;
      const permissionsGroup = this.roleForm.get('permissions') as FormGroup;
      const selectedPermIds = Object.keys(permissionsGroup.value).filter(key => permissionsGroup.get(key)?.value);
      
      const newRole: Partial<Role> = {
        name: formValue.name,
        identification: formValue.baseRole, // Use the same identification as base role
        level: 'CUSTOM',
        permissions: selectedPermIds, // Array of Permission ObjectIds
        baseRole: [formValue.baseRole]
        // subscriberId will be automatically added by the interceptor
      };

      this.roleService.createCustomRole(newRole).subscribe({
        next: (createdRole: RoleWithPermissions) => {
          this.closeDrawer();
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Role created successfully'
          });
          
          // Refresh all role data to show the new role
          this.refreshAllRoleData();
        },
        error: (error: any) => {
          console.error('Error creating role:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to create role'
          });
        }
      });
    }
  }

  isPermissionSelected(permissionId: string): boolean {
    const permissionsGroup = this.roleForm.get('permissions') as FormGroup;
    return permissionsGroup.get(permissionId)?.value || false;
  }

  hasPermission(role: RoleWithPermissions, permissionId: string): boolean {
    return role.permissions.some(perm => perm._id === permissionId);
  }

  getCategoryPermissionCount(role: RoleWithPermissions, category: string): number {
    const categoryPermissions = this.permissionsByCategory[category] || [];
    return categoryPermissions.filter(perm => this.hasPermission(role, perm._id)).length;
  }

  getSelectedPermissionsInCategory(category: string): number {
    const categoryPermissions = this.permissionsByCategory[category] || [];
    const permissionsGroup = this.roleForm.get('permissions') as FormGroup;
    return categoryPermissions.filter(perm => permissionsGroup.get(perm._id)?.value).length;
  }

  // Getter for base role permissions in a category
  getBaseRolePermissionsInCategory(category: string): Permission[] {
    if (!this.selectedBaseRole || !this.baseRolePermissions[this.selectedBaseRole]) {
      return [];
    }
    return this.baseRolePermissions[this.selectedBaseRole].filter(perm => perm.category === category);
  }

  onBaseRoleChange(baseRole: string): void {
    this.selectedBaseRole = baseRole;
    
    // Find the base role from global roles or subscriber roles to get its permissions
    let baseRoleData: RoleWithPermissions | undefined;
    
    // Check global roles first
    baseRoleData = this.globalRoles.find(role => role.identification === baseRole);
    
    // If not found in global roles, check subscriber roles
    if (!baseRoleData) {
      for (const subscriberId in this.subscriberRoles) {
        baseRoleData = this.subscriberRoles[subscriberId].find(role => role.identification === baseRole);
        if (baseRoleData) {
          break;
        }
      }
    }
    
    if (baseRoleData) {
      this.baseRolePermissions[baseRole] = baseRoleData.permissions;
      
      // Initialize selected permissions based on base role (all enabled by default)
      const permissionsGroup = this.roleForm.get('permissions') as FormGroup;
      
      // Clear existing permission controls
      Object.keys(permissionsGroup.controls).forEach(key => {
        permissionsGroup.removeControl(key);
      });
      
      // Add all base role permissions as enabled by default
      baseRoleData.permissions.forEach(perm => {
        permissionsGroup.addControl(perm._id, this.fb.control(true)); // Initialize as true (enabled)
      });
    } else {
      console.error(`Base role ${baseRole} not found`);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `Base role ${baseRole} not found`
      });
    }
  }

  // Pagination and search methods
  onPageChange(event: any): void {
    this.currentPage = event.page + 1; // PrimeNG uses 0-based indexing, convert to 1-based for backend
    this.pageSize = event.rows;
    this.loadPagedSubscribers();
  }

  applySearch(): void {
    this.currentPage = 1; // Reset to first page when searching
    this.loadPagedSubscribers();
  }

  // Debounced search input
  private searchTimeout: any;
  onSearchInput(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch();
    }, 500); // Wait 500ms after user stops typing
  }

  // Delete a custom role
  deleteRole(role: RoleWithPermissions, subscriberId: string): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`,
      header: 'Delete Role',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      accept: () => {
        this.performDeleteRole(role._id, subscriberId);
      }
    });
  }

  private performDeleteRole(roleId: string, subscriberId: string): void {
    this.roleService.deleteRole(roleId).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Role deleted successfully'
        });
        
        // Refresh all role data to reflect the deletion
        this.refreshAllRoleData();
      },
      error: (error: any) => {
        console.error('Error deleting role:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.errorResponse?.errmsg || 'Failed to delete role'
        });
      }
    });
  }

  // Check if a role can be deleted (custom role with 0 users)
  canDeleteRole(role: RoleWithPermissions): boolean {
    return role.level === 'CUSTOM' && (role.userCount || 0) === 0;
  }

  // Refresh all role data after create/delete operations
  private refreshAllRoleData(): void {
    // Refresh global roles
    this.loadGlobalRoles();
    
    // Refresh subscriber roles
    this.loadSubscriberRoles();
  }

  // Subscribe to subscription context changes
  private subscribeToSubscriptionContext(): void {
    this.subscriptionContextService.selectedSubscriber$.subscribe(
      (subscription: SubscriptionContext) => {
        this.selectedSubscription = subscription;
        // Update available base roles when subscription context changes
        this.updateAvailableBaseRoles();
      }
    );
  }

  // Check if a subscription is selected
  hasSelectedSubscription(): boolean {
    return this.selectedSubscription?.subscriberId != null;
  }

  // Get permission display name
  getPermissionDisplayName(permission: Permission): string {
    return permission.name;
  }
}
